import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createSocialPostComment,
  deleteSocialPostComment,
} from '@/src/actions/social-comments';

type Vars = {
  postId: number;
  content: string;
  parentId?: number | null;
  taggedUserId?: number | null;
  taggedUserUniqueLink?: string | null;
  replyToCommentId?: number | null;
};

export function useCreateSocialPostCommentMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (vars: Vars) => {
      const { postId, content, parentId, taggedUserId, replyToCommentId } =
        vars;
      return createSocialPostComment(postId, {
        content,
        parentId,
        taggedUserId,
        replyToCommentId,
      });
    },

    onSuccess: (result: any, vars) => {
      const { postId, parentId, taggedUserUniqueLink, replyToCommentId } = vars;

      const createdRaw = result?.data?.comment ?? result?.data ?? result;
      const isRoot = !parentId;

      const key = isRoot
        ? (['postComments', postId, 'root'] as const)
        : (['postComments', postId, 'replies', parentId] as const);

      const createdComment = {
        ...createdRaw,
        taggedUserUniqueLink:
          createdRaw?.taggedUserUniqueLink ?? taggedUserUniqueLink ?? null,
        replyToCommentId:
          createdRaw?.replyToCommentId ?? replyToCommentId ?? null,
      };

      qc.setQueryData(key, (old: any) => {
        if (!old?.pages?.length) {
          return {
            pageParams: [null],
            pages: [
              {
                data: { comments: [createdComment], page: 1, perPage: 10 },
                error: null,
              },
            ],
          };
        }

        const lastIndex = old.pages.length - 1;
        const lastPage = old.pages[lastIndex];
        const lastComments = lastPage?.data?.comments ?? [];

        const nextLastPage = {
          ...lastPage,
          data: {
            ...lastPage.data,
            comments: [...lastComments, createdComment],
          },
        };

        const nextPages = old.pages.slice();
        nextPages[lastIndex] = nextLastPage;

        return { ...old, pages: nextPages };
      });

      if (!isRoot && parentId) {
        bumpRepliesCountForCommentEverywhere(qc, postId, parentId, 1);
      }
    },
  });
}

export function useDeleteSocialPostCommentMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: number) => {
      return deleteSocialPostComment(commentId);
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['postComments'] });
      qc.invalidateQueries({ queryKey: ['socialPosts'] });
    },
  });
}

function bumpRepliesCountForCommentEverywhere(
  qc: ReturnType<typeof useQueryClient>,
  postId: number,
  targetCommentId: number,
  delta: number
) {
  qc.setQueriesData(
    {
      predicate: (q) => {
        const k = q.queryKey;
        return (
          Array.isArray(k) &&
          k[0] === 'postComments' &&
          Number(k[1]) === Number(postId)
        );
      },
    },
    (old: any) => {
      if (!old?.pages?.length) return old;

      const pages = old.pages.map((p: any) => {
        const comments = p?.data?.comments ?? [];
        const next = comments.map((c: any) =>
          Number(c.id) === Number(targetCommentId)
            ? { ...c, repliesCount: (c.repliesCount ?? 0) + delta }
            : c
        );

        return { ...p, data: { ...p.data, comments: next } };
      });

      return { ...old, pages };
    }
  );
}
